import { useEffect, useMemo, useRef, useState, type FocusEvent, type PointerEvent } from 'react';
import { EmailPattern } from '@/utils/emailUtil';
import { isValidPhone, PHONE_MESSAGE } from '@/utils/phoneUtil';
import { isValidWebsite, WEBSITE_MESSAGE } from '@/utils/websiteUtil';
import PrimaryButton from '@/components/common/buttons/PrimaryButton';
import OutlinedButton from '@/components/common/buttons/OutlinedButton';
import { Accordion, Box, HStack, Input, Stack, Text, VStack } from '@chakra-ui/react';
import {
	useForm,
	type Control,
	type FieldErrors,
	type UseFormHandleSubmit,
	type UseFormRegister,
} from 'react-hook-form';
import type { ContactTier, ContactWithCategory, PropertyContactWithDetails } from '@/client';
import { getContactFormValues, type ContactDraft } from '@/components/services/contact/contactFormValues';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';
import { Field } from '@/components/ui/field';
import ContactNotesField from '@/components/services/contact/ContactNotesField';
import RolePicker from '@/components/services/role/RolePicker';
import { useAddContact } from '@/hooks/service-providers/useAddContact';
import { useUpdateContact } from '@/hooks/service-providers/useUpdateContact';
import { useHousePermissions } from '@/hooks/useHasPermission';
import { usePropertyContacts } from '@/hooks/service-providers/usePropertyContacts';
import { accountUserIdOf, emailHeldByAnotherCard } from '@/components/services/model/serviceContacts';
import { useAutoFocusFirstField } from '@/hooks/useAutoFocusFirstField';
import { Title } from '@/components/ui/title';
import { InlineLinkButton } from '@/components/ui/inline-link-button';

interface ContactFormProps {
	houseId: number;
	categoryCode: string;
	heading?: string;
	existingContact?: PropertyContactWithDetails;
	/** Whether the form's slot is open. Drives reset + first-field focus on open. */
	active?: boolean;
	onCancel: () => void;
	/** The contact a submit created, so the caller can open it. Null when editing. */
	onSuccess: (createdContactId: number | null) => void;
}

interface UseContactFormControllerProps {
	houseId: number;
	categoryCode: string;
	existingContact?: PropertyContactWithDetails;
	/** The contact a submit created, so the caller can open it. Null when editing. */
	onSuccess: (createdContactId: number | null) => void;
}

const MORE_DETAILS = 'more-details';

/**
 * The fields measure themselves, not the viewport. The same form is a 440px pane on a wide screen,
 * where a viewport breakpoint would set two fields side by side in 200px each. Two fields go
 * side by side only once the form can give each a comfortable line.
 */
const FIELDS_CONTAINER = 'contact-form-fields';
const FIELDS_CONTAINER_CSS = { containerType: 'inline-size', containerName: FIELDS_CONTAINER } as const;
const PAIR_ROW_CSS = {
	flexDirection: 'column',
	[`@container ${FIELDS_CONTAINER} (min-width: 520px)`]: { flexDirection: 'row' },
} as const;
const LOCKED_FIELD = { cursor: 'default' } as const;

interface ContactFormFieldsProps {
	controller: ContactFormController;
	/** Starts a new contact from this one, which is the way to a different address on a locked card. */
	onAddAsNewContact?: () => void;
	/** Opens the folded details; a change of key repeats the request. */
	moreDetailsOpenRequest?: number;
}

export interface ContactFormData {
	name: string;
	company: string;
	email: string;
	phone: string;
	website: string;
	address: string;
	notes: string;
}

export interface ContactFormController {
	/** The role the contact is filed under, which a saved contact can be moved between. */
	categoryCode: string;
	setRole: (role: { code: string; id: number }) => void;
	/** The property whose catalog the role is picked from. */
	houseId: number;
	register: UseFormRegister<ContactFormData>;
	handleSubmit: UseFormHandleSubmit<ContactFormData>;
	control: Control<ContactFormData>;
	errors: FieldErrors<ContactFormData>;
	isEditing: boolean;
	/** The saved card being edited, which may keep its own address; null while adding. */
	contactId: number | null;
	/**
	 * A saved card that names an account keeps its address: the address is who the card is, and
	 * the backend refuses a change (backend slice 17). A different address is a new contact.
	 */
	isEmailLocked: boolean;
	isValid: boolean;
	isDirty: boolean;
	/** A submit has been tried, after which every error shows whatever has been left. */
	isSubmitted: boolean;
	isPending: boolean;
	submitContact: (data: ContactFormData) => Promise<boolean>;
	resetForm: () => void;
	/** Fills a new contact from a saved one, as the reader's own edits, for them to finish. */
	startFromDraft: (draft: ContactDraft) => void;
}

/** The fields with a format check, whose error waits for a value to have been left. */
type CheckedField = 'email' | 'phone' | 'website';

const NAME_OR_COMPANY_MESSAGE = 'Enter a name or company';

export const useContactFormController = (props: UseContactFormControllerProps): ContactFormController => {
	const { houseId, categoryCode: initialCategoryCode, existingContact, onSuccess } = props;
	const isEditing = !!existingContact;
	const isEmailLocked = existingContact ? accountUserIdOf(existingContact) != null : false;
	// An address that can change is checked against the other cards, so a save waits for them.
	const { data: savedContacts } = usePropertyContacts(houseId);
	const canCheckEmail = isEmailLocked || savedContacts !== undefined;
	/**
	 * Held, because a saved contact can be refiled while the form is open. An add is given its role
	 * by whatever opened the composer and never asks again, so the two share one value.
	 */
	const [categoryCode, setCategoryCode] = useState(initialCategoryCode);
	/** The id of the role picked here, which the picker hands over with it. */
	const [categoryId, setCategoryId] = useState<number | null>(null);
	// The drawer can swap the contact under a mounted form, and `useState` keeps its first value —
	// so the role would go on naming the contact before this one. `useForm` syncs its own fields
	// through `values`; this is the same for the one field it does not hold.
	useEffect(() => {
		setCategoryCode(initialCategoryCode);
		setCategoryId(null);
	}, [initialCategoryCode]);
	const formValues = useMemo(() => getContactFormValues(existingContact), [existingContact]);
	const {
		register,
		handleSubmit,
		control,
		reset,
		setValue,
		formState: { errors, isDirty, isValid, isSubmitted },
	} = useForm<ContactFormData>({
		mode: 'onTouched',
		reValidateMode: 'onChange',
		values: formValues,
	});
	/** The standing a new contact takes from the card it was started from; the form has no field for it. */
	const [draftTier, setDraftTier] = useState<ContactTier | null>(null);

	const addMutation = useAddContact(houseId);
	const updateMutation = useUpdateContact(houseId);
	const isPending = addMutation.isPending || updateMutation.isPending;

	const submitContact = async (data: ContactFormData) => {
		if (!canCheckEmail) return false;
		try {
			if (existingContact) {
				// Only a role that actually moved: the endpoint refuses a contact that already serves
				// the target role here, and sending the one it is already on would be that refusal.
				const movedToRole = categoryCode !== existingContact.category.code ? categoryId : null;
				await updateMutation.mutateAsync({
					propertyContactId: existingContact.id,
					data: {
						override_name: data.name.trim() || null,
						override_company: data.company.trim() || null,
						override_phone: data.phone || null,
						...(isEmailLocked ? {} : { override_email: data.email || null }),
						notes: data.notes || null,
						...(movedToRole ? { category_id: movedToRole } : {}),
					},
				});
			} else {
				const name = data.name.trim();
				const company = data.company.trim();
				const contact: ContactWithCategory = {
					// TODO: send `name` only when a person's name was typed, and no `party_type`, once
					// the create body fills both itself (backend slice 15, part A). Until then a
					// company with no named person is filed under its own name, as a company.
					name: name || company,
					company: company || undefined,
					email: data.email || undefined,
					phone: data.phone || undefined,
					website: data.website || undefined,
					address: data.address || undefined,
					category_code: categoryCode,
					party_type: name ? 'person' : 'company',
					notes: data.notes || undefined,
					tier: draftTier ?? undefined,
				};
				const created = await addMutation.mutateAsync(contact);
				onSuccess(created?.id ?? null);
				return true;
			}
			onSuccess(null);
			return true;
		} catch {
			// Error handling is done in the hooks
			return false;
		}
	};

	return {
		register,
		handleSubmit,
		control,
		errors,
		isEditing,
		contactId: existingContact?.id ?? null,
		isEmailLocked,
		isDirty,
		isValid: isValid && canCheckEmail,
		isSubmitted,
		isPending,
		categoryCode,
		houseId,
		setRole: (role: { code: string; id: number }) => {
			setCategoryCode(role.code);
			setCategoryId(role.id);
		},
		submitContact,
		resetForm: () => {
			reset(formValues);
			setDraftTier(null);
			setCategoryCode(initialCategoryCode);
			setCategoryId(null);
		},
		startFromDraft: ({ tier, ...fields }) => {
			for (const [field, value] of Object.entries(fields) as [keyof ContactFormData, string | undefined][]) {
				if (value) setValue(field, value, { shouldDirty: true });
			}
			setDraftTier(tier ?? null);
		},
	};
};

export const ContactFormFields = (props: ContactFormFieldsProps) => {
	const { controller, moreDetailsOpenRequest, onAddAsNewContact } = props;
	const { canManageNetwork: canManage } = useHousePermissions();
	const { register, control, errors, isEditing, isEmailLocked, categoryCode, setRole, houseId, contactId } =
		controller;
	const { data: contactsData, isError: isContactsError, refetch: refetchContacts } = usePropertyContacts(houseId);
	const emailCheckFailed = isContactsError && !contactsData && !isEmailLocked;
	/**
	 * The format checks that have been left with something typed in them. `onTouched` checks every
	 * keystroke once a field has been left at all, so one left empty and come back to said "Invalid"
	 * at the first letter; a format error waits for a value to have been left, then follows edits.
	 */
	const [leftFilled, setLeftFilled] = useState<ReadonlySet<CheckedField>>(() => new Set());
	const trackLeaving = <Registration extends { onBlur: (event: FocusEvent<HTMLInputElement>) => unknown }>(
		field: CheckedField,
		registration: Registration,
	) => ({
		...registration,
		onBlur: (event: FocusEvent<HTMLInputElement>) => {
			void registration.onBlur(event);
			const filled = !!event.target.value.trim();
			setLeftFilled((current) => {
				if (current.has(field) === filled) return current;
				const next = new Set(current);
				if (filled) next.add(field);
				else next.delete(field);
				return next;
			});
		},
	});
	/** "Email is required" still shows on leaving it empty; a submit shows everything. */
	const shownError = (field: CheckedField) => {
		const error = errors[field];
		if (!error) return undefined;
		return controller.isSubmitted || leftFilled.has(field) || error.type === 'required' ? error.message : undefined;
	};
	// While adding, website, address and notes are folded away: most contacts are a name and a way
	// to reach them. A saved contact is shown whole; a fold would only hide what is being edited.
	const [moreDetailsValue, setMoreDetailsValue] = useState<string[]>([]);
	// Only a request made while these fields are showing opens them. The key outlives the fields,
	// which remount for each contact, and the one they mount with has already been answered.
	const answeredRequestRef = useRef(moreDetailsOpenRequest);
	useEffect(() => {
		if (moreDetailsOpenRequest === answeredRequestRef.current) return;
		answeredRequestRef.current = moreDetailsOpenRequest;
		setMoreDetailsValue([MORE_DETAILS]);
	}, [moreDetailsOpenRequest]);

	// Name, company, phone and email are correctable on a saved contact — the write lands as an
	// override on this property's association, leaving the registry holding what the documents
	// read. Website and address have no override column, so they stay read-only once saved.
	// One of the two is required, and either will do. The rule sits on the name, so its message
	// shows once; the company re-runs it as it changes.
	const nameFields = (
		<Stack css={PAIR_ROW_CSS} gap={4} w="full" align="flex-start">
			<Field label="Name" flex={1} minW={0} invalid={!!errors.name} errorText={errors.name?.message}>
				<Input
					{...register('name', {
						validate: (value, values) =>
							value.trim().length > 0 || values.company.trim().length > 0 || NAME_OR_COMPANY_MESSAGE,
					})}
					placeholder="Name"
					borderRadius="full"
				/>
			</Field>

			<Field label="Company" flex={1} minW={0} invalid={!!errors.company} errorText={errors.company?.message}>
				<Input
					{...register('company', { deps: ['name'] })}
					placeholder="Company or organisation"
					borderRadius="full"
				/>
			</Field>
		</Stack>
	);

	const moreDetails = (
		<VStack gap={4} align="stretch">
			<Field label="Website" invalid={!!shownError('website')} errorText={shownError('website')}>
				<Input
					inputMode="url"
					{...trackLeaving(
						'website',
						register('website', {
							validate: (value) => !value.trim() || isValidWebsite(value) || WEBSITE_MESSAGE,
						}),
					)}
					placeholder="www.website.co.nz"
					borderRadius="full"
					disabled={isEditing}
					_disabled={LOCKED_FIELD}
				/>
			</Field>

			<Field label="Address" invalid={!!errors.address} errorText={errors.address?.message}>
				<AddressAutocomplete<ContactFormData>
					name="address"
					control={control}
					placeholder="123 Main Street"
					country="nz"
					required={false}
					borderRadius="full"
					disabled={isEditing}
					_disabled={LOCKED_FIELD}
				/>
			</Field>

			{canManage && (
				<Field label="Private notes" invalid={!!errors.notes} errorText={errors.notes?.message}>
					<ContactNotesField
						control={control}
						register={register}
						placeholder="What do they do? Service, install, repair, etc."
					/>
				</Field>
			)}
		</VStack>
	);

	return (
		<VStack gap={4} align="stretch" css={FIELDS_CONTAINER_CSS}>
			{nameFields}

			{/* Only on a saved contact. While adding, the role is the slot or the composer's own
			    Service step, asked before these fields and not repeated here. */}
			{isEditing ? (
				<Field label="Service">
					<RolePicker
						houseId={houseId}
						value={categoryCode}
						// The role it is filed under now, which this property need not still offer.
						unlistedValue={categoryCode}
						canEdit={canManage}
						onChange={setRole}
					/>
				</Field>
			) : null}

			<Stack css={PAIR_ROW_CSS} gap={4} w="full" align="flex-start">
				<Field
					label="Email"
					required
					flex={1}
					minW={0}
					invalid={!!shownError('email') || emailCheckFailed}
					errorText={
						shownError('email') ??
						(emailCheckFailed ? (
							<span>
								Couldn't check this email against saved contacts.
								<InlineLinkButton ml={2} onClick={() => void refetchContacts()}>
									Try again
								</InlineLinkButton>
							</span>
						) : undefined)
					}
					helperText={
						isEmailLocked ? (
							<>
								Linked to an account. To use a different address,{' '}
								{onAddAsNewContact ? (
									<InlineLinkButton onClick={onAddAsNewContact}>
										add them as a new contact
									</InlineLinkButton>
								) : (
									'add them as a new contact'
								)}
								.
							</>
						) : undefined
					}
				>
					<Input
						type="email"
						{...trackLeaving(
							'email',
							register('email', {
								required: 'Email is required',
								// Checked and saved without the spaces a paste brings along.
								setValueAs: (value: string) => value.trim(),
								pattern: EmailPattern,
								validate: (value) =>
									emailHeldByAnotherCard(contactsData, value, contactId ?? undefined) ?? true,
							}),
						)}
						placeholder="email@example.com"
						borderRadius="full"
						disabled={isEmailLocked}
						_disabled={LOCKED_FIELD}
					/>
				</Field>

				<Field
					label="Phone number"
					flex={1}
					minW={0}
					invalid={!!shownError('phone')}
					errorText={shownError('phone')}
				>
					<Input
						type="tel"
						{...trackLeaving(
							'phone',
							register('phone', {
								// Optional, so only a value somebody typed is checked.
								validate: (value) => !value.trim() || isValidPhone(value) || PHONE_MESSAGE,
							}),
						)}
						placeholder="027 000 0000"
						borderRadius="full"
					/>
				</Field>
			</Stack>

			{isEditing ? (
				moreDetails
			) : (
				<Accordion.Root
					variant="plain"
					collapsible
					value={moreDetailsValue}
					onValueChange={(details) => setMoreDetailsValue(details.value)}
				>
					<Accordion.Item value={MORE_DETAILS} borderWidth="0">
						{/* A rule with the toggle in it, rather than a heading: the fields below are the same
						    form continued, not a section of their own. */}
						<Accordion.ItemTrigger px={0} py={0} cursor="pointer" _hover={{ bg: 'transparent' }}>
							<HStack w="100%" gap={3}>
								<Box flex={1} h="1px" bg="border.default" />
								<HStack gap={1} color="fg.muted" _hover={{ color: 'brand.fg' }}>
									<Accordion.ItemContext>
										{(item) => <Text textStyle="label.sm">{item.expanded ? 'Less' : 'More'}</Text>}
									</Accordion.ItemContext>
									<Accordion.ItemIndicator />
								</HStack>
								<Box flex={1} h="1px" bg="border.default" />
							</HStack>
						</Accordion.ItemTrigger>
						<Accordion.ItemContent>
							<Accordion.ItemBody px={0} pt={4} pb={0}>
								{moreDetails}
							</Accordion.ItemBody>
						</Accordion.ItemContent>
					</Accordion.Item>
				</Accordion.Root>
			)}
		</VStack>
	);
};

/**
 * Form for adding or editing a contact.
 * Validates required fields and handles connect confirmation.
 */
const ContactForm = (props: ContactFormProps) => {
	const { houseId, categoryCode, heading, existingContact, active = false, onCancel, onSuccess } = props;
	const controller = useContactFormController({ houseId, categoryCode, existingContact, onSuccess });
	// On open, clear stale values/validation and focus the first field.
	const formRef = useAutoFocusFirstField<HTMLDivElement>(active, controller.resetForm);
	const handleCancelPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
		event.preventDefault();
		onCancel();
	};

	return (
		<Box
			ref={formRef}
			bg="surface.default"
			px={4}
			pt={4}
			pb={4}
			borderBottomRadius="24px"
			as="form"
			onSubmit={controller.handleSubmit(controller.submitContact)}
		>
			{heading && <Title mb={4}>{heading}</Title>}

			<ContactFormFields controller={controller} />

			<HStack gap={2} pt={6}>
				<OutlinedButton flex="1" onPointerDown={handleCancelPointerDown} onClick={onCancel} type="button">
					Cancel
				</OutlinedButton>
				<PrimaryButton
					textStyle="body.sm"
					fontWeight="normal"
					flex="1"
					disabled={!controller.isValid}
					type="submit"
					loading={controller.isPending}
				>
					{controller.isEditing ? 'Save' : 'Add contact'}
				</PrimaryButton>
			</HStack>
		</Box>
	);
};

export default ContactForm;
